import React, { memo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Check, ArrowRight, Users, DollarSign, TrendingUp, Building2, ChevronLeft, ChevronRight } from 'lucide-react';

interface OnboardingStepProps {
  step: {
    id: string;
    title: string;
    description: string;
    icon: React.ComponentType<any>;
    required: boolean;
  };
  isActive: boolean;
  isCompleted: boolean;
  onClick: () => void;
  children?: React.ReactNode;
}

export const OnboardingStep = memo(function OnboardingStep({
  step,
  isActive,
  isCompleted,
  onClick,
  children
}: OnboardingStepProps) {
  const Icon = step.icon;

  return (
    <Card
      className={`cursor-pointer transition-all duration-300 ease-out transform ${
        isActive
          ? 'ring-2 ring-blue-500 shadow-xl border-blue-200 scale-[1.02] bg-blue-50/30 dark:bg-blue-950/10'
          : isCompleted
          ? 'border-green-200 bg-green-50/50 dark:bg-green-950/20 hover:scale-[1.01]'
          : 'hover:border-blue-200 hover:shadow-lg hover:scale-[1.01] hover:-translate-y-0.5'
      }`}
      onClick={onClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${
              isActive
                ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30 scale-110'
                : isCompleted
                ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md shadow-green-500/20'
                : 'bg-muted text-muted-foreground group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30'
            }`}>
              {isCompleted ? (
                <Check className="w-6 h-6 animate-scale-in" />
              ) : (
                <Icon className={`w-6 h-6 ${isActive ? 'animate-pulse' : ''}`} />
              )}
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">{step.title}</CardTitle>
              <CardDescription className="text-sm">{step.description}</CardDescription>
            </div>
          </div>
          {step.required && (
            <Badge variant="secondary" className="text-xs font-medium">
              Required
            </Badge>
          )}
        </div>
      </CardHeader>

      {children && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );
});

interface OnboardingStepContentProps {
  step: {
    id: string;
    title: string;
    description: string;
    icon: React.ComponentType<any>;
  };
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export const OnboardingStepContent = memo(function OnboardingStepContent({
  step,
  children,
  actions
}: OnboardingStepContentProps) {
  const Icon = step.icon;

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center space-y-4 animate-fade-in-down">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-4 shadow-lg shadow-blue-500/20 animate-scale-in">
          <Icon className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
          {step.title}
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
          {step.description}
        </p>
      </div>

      <div className="max-w-4xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
        {children}
      </div>

      {actions && (
        <div className="flex justify-center pt-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {actions}
        </div>
      )}
    </div>
  );
});

interface OnboardingNavigationProps {
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  nextLabel?: string;
  prevLabel?: string;
}

export function OnboardingNavigation({
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  canGoNext,
  canGoPrev,
  nextLabel = "Next",
  prevLabel = "Previous"
}: OnboardingNavigationProps) {
  return (
    <div className="border-t border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-lg shadow-slate-900/5">
      <div className="container mx-auto px-6 py-5">
        <div className="flex justify-between items-center max-w-4xl mx-auto gap-4">
          <Button
            variant="outline"
            onClick={onPrev}
            disabled={!canGoPrev}
            className="gap-2 transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <ChevronLeft className="w-4 h-4" />
            {prevLabel}
          </Button>

          <div className="flex gap-2.5 items-center">
            {Array.from({ length: totalSteps }, (_, index) => (
              <div
                key={index}
                className={`transition-all duration-300 ease-out ${
                  index === currentStep
                    ? 'w-8 h-2 bg-blue-500 rounded-full shadow-md shadow-blue-500/30'
                    : index < currentStep
                    ? 'w-2 h-2 bg-green-500 rounded-full shadow-sm shadow-green-500/20'
                    : 'w-2 h-2 bg-slate-300 dark:bg-slate-600 rounded-full'
                }`}
                style={{
                  animationDelay: `${index * 0.05}s`
                }}
              />
            ))}
          </div>

          <Button
            onClick={onNext}
            disabled={!canGoNext}
            className="gap-2 transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-lg shadow-blue-500/30"
          >
            {nextLabel}
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}